import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { motion } from 'motion/react';
import { forwardRef } from 'react';

const Collapsible = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

/**
 * Inner motion div that receives merged props from Radix (including data-state).
 * With forceMount, content stays in the DOM so we animate height open/closed
 * instead of unmounting before exit animation can run.
 */
type CollapsibleContentMotionProps = React.ComponentPropsWithoutRef<typeof motion.div> & {
  'data-state'?: 'open' | 'closed';
};

const CollapsibleContentMotion = forwardRef<HTMLDivElement, CollapsibleContentMotionProps>(({ children, style, ...mergedProps }, ref) => {
  const isOpen = mergedProps['data-state'] === 'open';
  return (
    <motion.div
      ref={ref}
      initial={false}
      animate={{
        height: isOpen ? 'auto' : 0,
        opacity: isOpen ? 1 : 0,
        marginTop: isOpen ? 12 : 0,
        minHeight: isOpen ? undefined : 0,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{
        overflow: 'hidden',
        marginBottom: 12,
        ...style,
      }}
      {...mergedProps}
    >
      {children}
    </motion.div>
  );
});
CollapsibleContentMotion.displayName = 'CollapsibleContentMotion';

const CollapsibleContent = forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ children, ...props }, ref) => (
  <CollapsiblePrimitive.CollapsibleContent forceMount {...props} ref={ref} asChild>
    <CollapsibleContentMotion>{children}</CollapsibleContentMotion>
  </CollapsiblePrimitive.CollapsibleContent>
));

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
